import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Building2, Loader } from 'lucide-react';
import api, { formatError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const roleLabels = { boss: 'Boss', manager: 'Manager', employee: 'Employee' };

export default function JoinInvite() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { registerWithInvite, user } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/invites/lookup/${code}`);
        setInvite(data);
        if (data.email) setForm((f) => ({ ...f, email: data.email }));
      } catch (err) {
        setError(formatError(err));
      }
    })();
  }, [code]);

  useEffect(() => {
    if (user) {
      toast.message('You are already signed in. Log out first to join with this invite.');
      navigate('/');
    }
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await registerWithInvite({ invite_code: code, ...form });
      toast.success(`Joined ${invite.workspace_name}`);
      navigate('/');
    } catch (err) { toast.error(formatError(err)); }
    finally { setLoading(false); }
  };

  if (error) {
    return (
      <div className="min-h-screen grid-pattern bg-[#F4F4F5] flex items-center justify-center p-6">
        <div className="w-full max-w-md bg-white border border-zinc-900 shadow-brutal p-8 text-center">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-red-600 mb-2">INVITE PROBLEM</div>
          <h2 className="font-display text-2xl font-black tracking-tighter mb-4">{error}</h2>
          <Link to="/register" className="inline-block bg-[#002FA7] text-white px-4 py-2 text-xs font-bold uppercase tracking-[0.2em]">Try another code →</Link>
        </div>
      </div>
    );
  }

  if (!invite) {
    return (
      <div className="min-h-screen grid-pattern bg-[#F4F4F5] flex items-center justify-center p-6">
        <div className="flex items-center gap-2 font-mono text-sm text-zinc-500"><Loader className="animate-spin" size={16} /> LOADING INVITE...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid-pattern bg-[#F4F4F5] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-zinc-900 shadow-brutal p-8">
        <div className="bg-[#002FA7] text-white p-4 mb-6">
          <div className="flex items-center gap-2 mb-2">
            <Building2 size={16} strokeWidth={2} />
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-80">YOU&apos;RE INVITED TO</div>
          </div>
          <div className="font-display text-3xl font-black tracking-tighter">{invite.workspace_name}</div>
          <div className="text-xs mt-2">As a <strong>{roleLabels[invite.role]}</strong>, invited by {invite.invited_by || 'admin'}.</div>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <Field label="Your full name" testid="join-name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Email" type="email" testid="join-email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} disabled={!!invite.email} />
          <Field label="Choose a password" type="password" testid="join-password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} />
          <button type="submit" disabled={loading} data-testid="join-submit" className="w-full bg-[#002FA7] text-white py-3 text-xs font-bold uppercase tracking-[0.2em] hover:bg-[#002088] disabled:opacity-60">
            {loading ? 'Joining...' : 'Accept & Join →'}
          </button>
        </form>
        <div className="mt-6 pt-5 border-t border-zinc-200 text-sm text-zinc-600">
          Already have an account? <Link to="/login" className="font-bold text-[#002FA7] underline">Sign in instead</Link> (you&apos;ll need to ask your admin to add you manually).
        </div>
      </div>
    </div>
  );
}

function Field({ label, testid, value, onChange, type = 'text', disabled = false }) {
  return (
    <div>
      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500 block mb-2">{label}</label>
      <input data-testid={testid} type={type} required value={value} onChange={(e) => onChange(e.target.value)} disabled={disabled}
        className="w-full border border-zinc-300 focus:border-[#002FA7] outline-none px-3 py-3 bg-white disabled:bg-zinc-100" />
    </div>
  );
}
